import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, CheckCircle, Clock, Sparkles, MessageSquare } from "lucide-react";
import axios from "axios";
import blogArticles from "@/data/blogArticles";
import BlogCoverImage from "@/components/BlogCoverImage";
import { LogoLight } from "@/components/brand/Logo";
import usePageTitle from "@/hooks/usePageTitle";

const API = process.env.REACT_APP_BACKEND_URL;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function BlogPage() {
  usePageTitle("Insights and Intelligence");
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [dbArticles, setDbArticles] = useState(null);
  const [featuredInsights, setFeaturedInsights] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/blogs`).then(r => setDbArticles(r.data || [])).catch(() => setDbArticles([]));
    axios.get(`${API}/api/insights/featured`).then(r => setFeaturedInsights(r.data || [])).catch(() => setFeaturedInsights([]));
  }, []);

  // Merge DB (AI-generated + admin) on top, then hardcoded fallback
  const articles = useMemo(() => {
    if (!dbArticles) return blogArticles;
    if (dbArticles.length === 0) return blogArticles;
    // Merge — DB items first (newest), then hardcoded (skip duplicates by slug)
    const dbSlugs = new Set(dbArticles.map(a => a.slug));
    return [...dbArticles, ...blogArticles.filter(a => !dbSlugs.has(a.slug))];
  }, [dbArticles]);

  const categories = ["All", ...new Set(articles.map(a => a.category))];

  const filtered = articles.filter(a => {
    const matchCat = cat === "All" || a.category === cat;
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div>
      {/* Branded light header — Version B */}
      <section className="bg-white pt-20 pb-4" data-testid="blog-branded-header">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between flex-wrap gap-3">
          <LogoLight width={180} height={42} />
          <span style={{ borderLeft: "3px solid #C9A84C", paddingLeft: "10px", fontFamily: "Georgia,serif", fontSize: "13px", color: "#0F1E35" }}>Blog</span>
        </div>
        <div className="max-w-5xl mx-auto px-4 mt-4" style={{ height: "1px", background: "rgba(15,30,53,0.08)" }} />
      </section>

      {/* Hero */}
      <section className="bg-navy pt-12 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Insights for HR Leaders
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-dm text-white/60 text-lg mb-8">
            Practical articles on competency design, talent development, and the future of HR
          </motion.p>
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles..." className="w-full pl-11 pr-4 py-3 rounded-lg font-dm text-sm focus:outline-none" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-cream py-6 border-b border-navy/5">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap gap-2 justify-center">
          {categories.map(c => (
            <button key={c} onClick={() => setCat(c)}
              className={`font-dm text-sm px-4 py-2 rounded-full transition-colors ${cat === c ? "bg-gold text-navy font-semibold" : "bg-white text-navy/60 hover:bg-navy/5"}`}
            >{c}</button>
          ))}
        </div>
      </section>

      {/* Featured */}
      {filtered.length > 0 && (
        <section className="bg-cream py-12">
          <div className="max-w-5xl mx-auto px-4">
            <Link to={`/blog/${filtered[0].slug}`} className="block">
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl overflow-hidden transition-shadow">
                {filtered[0].cover_image && (
                  <BlogCoverImage src={filtered[0].cover_image} alt={filtered[0].title} className="w-full h-64 sm:h-80 object-cover" />
                )}
                <div className="p-8 sm:p-12">
                  <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full mb-4">{filtered[0].category}</span>
                  <h2 className="font-playfair text-navy text-2xl sm:text-3xl font-bold mb-3">{filtered[0].title}</h2>
                  <p className="font-dm text-navy/60 text-sm mb-4 line-clamp-3">{filtered[0].content.substring(0, 200)}...</p>
                  <div className="flex items-center gap-3 font-dm text-navy/40 text-xs">
                    <span>{filtered[0].author}</span><span>{filtered[0].date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {filtered[0].read_time}</span>
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="bg-cream pb-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(1).map((a, i) => (
              <Link key={a.slug} to={`/blog/${a.slug}`}>
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-1 overflow-hidden transition-all h-full">
                  {a.cover_image && (
                    <BlogCoverImage src={a.cover_image} alt={a.title} className="w-full h-40 object-cover" />
                  )}
                  <div className="p-6">
                    <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full mb-3">{a.category}</span>
                    <h3 className="font-playfair text-navy font-semibold mb-2 line-clamp-2">{a.title}</h3>
                    <p className="font-dm text-navy/50 text-xs mb-4 line-clamp-3">{a.content.substring(0, 120)}...</p>
                    <div className="flex items-center gap-3 font-dm text-navy/40 text-xs mt-auto">
                      <span>{a.author}</span><span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.read_time}</span>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Community Spotlight (featured insights) */}
      {featuredInsights.length > 0 && (
        <section className="bg-white py-16 border-t border-navy/5" data-testid="community-spotlight">
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
              <div>
                <p className="font-dm text-gold text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2"><Sparkles className="w-3 h-3" /> Community Spotlight</p>
                <h2 className="font-playfair text-navy text-2xl sm:text-3xl font-bold">Voices from our community</h2>
              </div>
              <Link to="/insights" className="font-dm text-navy text-sm hover:text-gold flex items-center gap-1">
                <MessageSquare className="w-4 h-4" /> All insights →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredInsights.slice(0, 3).map((p, i) => (
                <motion.div key={p._id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-cream rounded-2xl shadow-sm hover:shadow-lg p-6 transition-all border-l-4 border-gold">
                  <span className="inline-block bg-navy text-gold font-dm text-[10px] px-2 py-0.5 rounded-full mb-3">★ Featured</span>
                  <h3 className="font-playfair text-navy text-lg font-semibold mb-2 line-clamp-2">{p.title}</h3>
                  <p className="font-dm text-navy/60 text-xs line-clamp-3 mb-4">{p.body}</p>
                  <p className="font-dm text-navy/50 text-xs"><span className="font-semibold text-navy">{p.author_name}</span> · {p.author_role || "Community Member"}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="bg-navy py-16">
        <div className="max-w-xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-white text-2xl font-bold mb-4">Never miss an insight. Subscribe to the RoleIQ newsletter.</h2>
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!email.trim() || !email.includes("@")) { setStatus("error"); setMessage("Please enter a valid email"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/newsletter/subscribe`, { email: email.trim() });
      setStatus("success"); setMessage(res.data.message); setEmail("");
    } catch (err) { setStatus("error"); setMessage(err?.response?.data?.detail || "Subscription failed"); }
    finally { setLoading(false); }
  };

  return status === "success" ? (
    <div className="flex items-center justify-center gap-2"><CheckCircle className="w-5 h-5 text-gold" /><span className="font-dm text-white text-sm">{message}</span></div>
  ) : (
    <div>
      <div className="flex gap-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" data-testid="newsletter-email" className="flex-1 px-4 py-3 rounded-lg font-dm text-sm focus:outline-none" />
        <button onClick={handleSubscribe} disabled={loading} data-testid="newsletter-submit" className="bg-gold text-navy font-dm font-bold px-6 py-3 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50">{loading ? "..." : "Subscribe"}</button>
      </div>
      {status === "error" && <p className="font-dm text-red-300 text-xs mt-2">{message}</p>}
    </div>
  );
}
